import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Pagina1 from '../answerComponents/visualComponent';
import Pagina2 from '../answerComponents/olfactoryComponent';
import Pagina3 from '../answerComponents/tasteComponent';
import Pagina4 from '../answerComponents/noteComponent';
import Pagina5 from '../answerComponents/orderComponent';
import bgImage from '../images/background.png';

const helpUrls = [
    '',
    '',
    '',
    {
        A: `${process.env.REACT_APP_SRC_URL}/respuesta-A.jpg`,
        B: `${process.env.REACT_APP_SRC_URL}/respuesta-B.jpg`,
        C: `${process.env.REACT_APP_SRC_URL}/respuesta-C.jpg`,
        D: `${process.env.REACT_APP_SRC_URL}/respuesta-D.jpg`,
    },
    '',
];

const PAGE_NAMES = ['visual', 'olfactory', 'taste', 'note', 'order'];

function AnIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'x':     return <svg {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
        case 'left':  return <svg {...p}><polyline points="15 18 9 12 15 6"/></svg>;
        case 'right': return <svg {...p}><polyline points="9 18 15 12 9 6"/></svg>;
        case 'help':  return <svg {...p}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
        case 'close-popup': return <svg {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
        default: return null;
    }
}

export default function SliderComponent() {
    const { t, i18n } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [test, setTest] = useState(null);
    const [currentTest, setCurrentTest] = useState(null);
    const [helpPopupVisible, setHelpPopupVisible] = useState(false);
    const [popupImage, setPopupImage] = useState(null);
    const [ready, setReady] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [retryKey, setRetryKey] = useState(0);
    const navigate = useNavigate();
    const saveAnswersRef = useRef(null);

    useEffect(() => { requestAnimationFrame(() => setReady(true)); }, []);

    useEffect(() => {
        const ct = JSON.parse(localStorage.getItem('currentTest'));
        if (ct) { fetchTestDetails(ct.id); }
    }, [retryKey]);

    const fetchTestDetails = (testId) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/getTest/${testId}`, true);
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
                if (xhr.status === 200) {
                    const data = JSON.parse(xhr.responseText);
                    setTest(data.object);
                    setCurrentTest(data.title);
                } else {
                    setFetchError(true);
                }
            }
        };
        xhr.send();
    };

    const handleHelpClick = () => {
        let imageUrl;
        if (currentPage === 1 && currentTest) {
            const suffix = i18n.language === 'en' ? '_en' : '';
            imageUrl = `${process.env.REACT_APP_SRC_URL}/respuesta-${currentTest}${suffix}.jpg`;
        } else {
            imageUrl = helpUrls[currentPage - 1];
        }
        if (imageUrl) { setPopupImage(imageUrl); setHelpPopupVisible(true); }
    };

    const renderContent = () => {
        if (!test) return <div className="an-slide"><p style={{ opacity: .5, textAlign: 'center', padding: '40px 0' }}>{t('common.loading')}</p></div>;
        switch (currentPage) {
            case 1: return <Pagina4 first={test[0]} second={test[1]} actual={currentTest?.title} />;
            case 2: return <Pagina2 first={test[2]} second={test[3]} third={test[4]} fourth={test[5]} actual={currentTest?.title} />;
            case 3: return <Pagina3 actual={currentTest?.title} />;
            case 4: return <Pagina1 actual={currentTest?.title} first={test[11]} second={test[12]} onSaveAnswers={(fn) => saveAnswersRef.current = fn} />;
            case 5: return <Pagina5 />;
            default: return null;
        }
    };

    const wineBadge = currentTest?.title || (JSON.parse(localStorage.getItem('currentTest') || '{}'))?.title || '';

    return (
        <div className={`an-stage${ready ? ' an-ready' : ''}`}>
            <div className="an-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="an-vignette" />
            <div className="an-grain" />

            <div className="an-topbar">
                <button className="an-icon-btn" onClick={() => navigate('/choose-answer')} aria-label={t('common.close')}>
                    <AnIcon name="x" size={16} />
                </button>
                <div className="an-topbar-center">
                    <span className="an-topbar-title">{t('test.correct_answers')}</span>
                    {wineBadge && <span className="an-wine-badge">{t('choose_wine.wine')} {wineBadge}</span>}
                </div>
                <button className="an-icon-btn" onClick={handleHelpClick} aria-label={t('common.help')}>
                    <AnIcon name="help" size={16} />
                </button>
            </div>

            <div className="an-content">
                {renderContent()}
            </div>

            <div className="an-nav">
                <button
                    className="an-nav-btn"
                    onClick={() => setCurrentPage(p => p - 1)}
                    disabled={currentPage === 1}
                    aria-label={t('common.back')}
                >
                    <AnIcon name="left" size={18} />
                </button>
                <span className="an-page-indicator">
                    <span>{currentPage}</span> / {PAGE_NAMES.length}
                </span>
                <button
                    className="an-nav-btn"
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={currentPage === PAGE_NAMES.length}
                    aria-label={t('common.next')}
                >
                    <AnIcon name="right" size={18} />
                </button>
            </div>

            {fetchError && (
                <div className="page-fetch-error">
                    <p>{t('common.fetch_error')}</p>
                    <button onClick={() => { setFetchError(false); setRetryKey(k => k + 1); }}>
                        {t('common.retry')}
                    </button>
                </div>
            )}

            {helpPopupVisible && popupImage && (
                <div className="an-popup-overlay" onClick={() => { setHelpPopupVisible(false); setPopupImage(null); }}>
                    <div className="an-popup" onClick={e => e.stopPropagation()}>
                        <button className="an-popup-close" onClick={() => { setHelpPopupVisible(false); setPopupImage(null); }}>
                            <AnIcon name="close-popup" size={14} />
                        </button>
                        <img src={popupImage} alt={t('common.help')} className="an-popup-img" />
                    </div>
                </div>
            )}
        </div>
    );
}
